import type { MediaImage as MediaImageType, Image as ShopifyImage, Video as VideoType } from "@shopify/hydrogen-react/storefront-api-types";
import classNames from "classnames";
import React, { useState } from "react";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import { Zoom } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import useScrollLock from "@repo/shopify/hooks/useScrollLock";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Video from "@atoms/Video";

import type { IRestrictedMediaEdge } from "@molecules/MediaSlider";

import { URLS } from "@const/urls";

const ZOOM_RATIO = 2;

interface IProps {
  media: IRestrictedMediaEdge[];
  thumbnails: { image: ShopifyImage; mediaIndex: number }[];
  initialSlide?: number;
  onClose: () => void;
}

export default function ZoomImageSlider({ media, thumbnails, initialSlide = 0, onClose }: IProps) {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);

  useScrollLock();

  /** 확대 중에도 Swiper 파라미터를 바꾸지 않고, 인덱스로만 좌우 이동을 막는다 */
  const syncSlideBoundary = (swiper: SwiperClass) => {
    swiper.allowSlidePrev = swiper.activeIndex > 0;
    swiper.allowSlideNext = swiper.activeIndex < media.length - 1;
  };

  const toggleZoom = () => {
    if (!swiperInstance) return;

    const zoom = swiperInstance.zoom;
    const currentScale = typeof zoom.scale === "number" ? zoom.scale : parseFloat(String(zoom.scale ?? 1));

    if (currentScale > 1) {
      zoom.out();
      setIsZoomed(false);
      return;
    }

    zoom.in(ZOOM_RATIO);
    setIsZoomed(true);
  };

  return (
    <section className="dark:bg-dj-black fixed inset-0 z-70 bg-white">
      <Swiper
        className={classNames("size-full [&_.swiper-slide-zoomed]:touch-none", isZoomed ? "cursor-grab" : "cursor-zoom-in")}
        initialSlide={initialSlide}
        modules={[Zoom]}
        onClick={toggleZoom}
        onSlideChange={(swiper) => {
          setCurrentSlide(swiper.activeIndex);
          setIsZoomed(false);
          syncSlideBoundary(swiper);
        }}
        onSwiper={(swiper) => {
          setSwiperInstance(swiper);
          syncSlideBoundary(swiper);
        }}
        slidesPerView={1}
        spaceBetween={0}
        zoom={{ maxRatio: ZOOM_RATIO }}
      >
        {media.map(({ node }) => (
          <SwiperSlide key={node.id}>
            <div className="swiper-zoom-container flex h-dvh w-full items-center justify-center">
              {node.mediaContentType === "IMAGE" ? (
                <Image
                  alt={(node as MediaImageType).image?.altText || ""}
                  className="dark:bg-dj-black! h-dvh w-full bg-white! [&>img]:object-contain!"
                  height={(node as MediaImageType).image?.height || 1707}
                  loading="eager"
                  src={(node as MediaImageType).image?.url || ""}
                  width={(node as MediaImageType).image?.width || 1280}
                />
              ) : (
                <Video
                  className="h-dvh w-full bg-white! object-contain!"
                  height={(node as VideoType).previewImage?.height || 1707}
                  poster={(node as VideoType).previewImage?.url || ""}
                  src={(node as VideoType)?.sources?.[1]?.url || ""}
                  width={(node as VideoType).previewImage?.width || 1280}
                />
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="pc:px-5 pc:h-13 pc:min-h-13 absolute top-0 left-0 z-10 mx-auto flex h-13.5 min-h-13.5 w-full items-center justify-between p-4 text-black dark:text-white">
        <Link className="text-title font-features-normal flex w-fit leading-none" href={URLS.HOME}>
          thisisneverthat®
        </Link>
        <Button as="text" onClick={onClose}>
          <Icon className="size-4" name="Close" />
        </Button>
      </div>

      {thumbnails.length ? (
        <section className="scrollbar-hide absolute top-25 right-5 z-10 flex h-fit max-h-[calc(100vh-260px)] w-[calc((100vw-(clamp(1.25rem,2vw,2.5rem)*11+2.5rem))/24)] max-w-12.5 min-w-10 flex-col items-end gap-1 overflow-x-scroll overflow-y-scroll">
          {thumbnails.map(({ image, mediaIndex }) => (
            <Button
              as="wrapper"
              className="relative"
              key={image.id}
              onClick={() => {
                swiperInstance?.slideTo(mediaIndex);
              }}
            >
              <Image
                alt={image?.altText || ""}
                height={image?.height || 1707}
                src={image?.url.replaceAll(".jpg", "_128x.jpg") || ""}
                width={image?.width || 1280}
              />
              {currentSlide === mediaIndex && <div className="absolute top-0 left-0 size-full bg-black/10" />}
            </Button>
          ))}
        </section>
      ) : null}
    </section>
  );
}
